import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  FileText,
  Euro,
  AlertTriangle,
  Plus,
  ArrowRight,
  TrendingUp,
  Calendar,
  Receipt,
  Wallet
} from 'lucide-react';
import toast from 'react-hot-toast';
import { dashboardAPI, invoicesAPI, expensesAPI } from '../../services/api';
import LoadingSpinner from '../Common/LoadingSpinner';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import './Dashboard.css';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [monthlyRevenue, setMonthlyRevenue] = useState([]);
  const [expenseStats, setExpenseStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      const [statsResponse, revenueResponse, expenseResponse] = await Promise.all([
        dashboardAPI.getStats(),
        dashboardAPI.getMonthlyRevenue(new Date().getFullYear()),
        expensesAPI.getStats().catch(() => ({ data: null })),
      ]);

      setStats(statsResponse.data);
      setMonthlyRevenue(revenueResponse.data);
      setExpenseStats(expenseResponse.data);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast.error('Fehler beim Laden der Dashboard-Daten');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return format(parseISO(dateString), 'dd.MM.yyyy', { locale: de });
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      draft: 'status-badge status-draft',
      sent: 'status-badge status-sent',
      paid: 'status-badge status-paid',
      overdue: 'status-badge status-overdue'
    };
    return classes[status] || 'status-badge';
  };

  const getStatusText = (status) => {
    const texts = {
      draft: 'Entwurf',
      sent: 'Versendet',
      paid: 'Bezahlt',
      overdue: 'Überfällig'
    };
    return texts[status] || status;
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <LoadingSpinner size="large" />
        <p>Lade Dashboard...</p>
      </div>
    );
  }

  const currentMonth = new Date().getMonth();
  const currentMonthRevenue = monthlyRevenue[currentMonth]?.revenue || 0;
  const lastMonthRevenue = monthlyRevenue[currentMonth - 1]?.revenue || 0;
  const revenueChange = lastMonthRevenue > 0
    ? ((currentMonthRevenue - lastMonthRevenue) / lastMonthRevenue * 100)
    : 0;

  return (
    <div className="dashboard">
      <div className="content-header">
        <div>
          <h1>Dashboard</h1>
          <p>Übersicht über Ihre Geschäftstätigkeit</p>
        </div>
        <div className="header-actions">
          <Link to="/customers/new" className="btn btn-secondary">
            <Plus size={16} />
            Neuer Kunde
          </Link>
          <Link to="/invoices/new" className="btn btn-primary">
            <Plus size={16} />
            Neue Rechnung
          </Link>
        </div>
      </div>

      <div className="content-body">
        {/* Statistics Cards */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon stat-icon-blue">
              <Users size={24} />
            </div>
            <div className="stat-content">
              <h3>Kunden</h3>
              <div className="stat-value">{stats?.total_customers || 0}</div>
              <p className="stat-subtitle">
                {stats?.active_customers || 0} aktiv
              </p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon stat-icon-green">
              <Euro size={24} />
            </div>
            <div className="stat-content">
              <h3>Monatsumsatz</h3>
              <div className="stat-value">
                {formatCurrency(stats?.monthly_revenue || 0)}
              </div>
              <p className={`stat-subtitle ${revenueChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {revenueChange >= 0 ? '+' : ''}{revenueChange.toFixed(1)}% vs. Vormonat
              </p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon stat-icon-purple">
              <FileText size={24} />
            </div>
            <div className="stat-content">
              <h3>Offene Rechnungen</h3>
              <div className="stat-value">{stats?.outstanding_invoices || 0}</div>
              <p className="stat-subtitle">
                {formatCurrency(stats?.outstanding_amount || 0)}
              </p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon stat-icon-yellow">
              <AlertTriangle size={24} />
            </div>
            <div className="stat-content">
              <h3>Überfällige Rechnungen</h3>
              <div className="stat-value">
                {stats?.recent_invoices?.filter(inv => inv.status === 'overdue').length || 0}
              </div>
              <p className="stat-subtitle">
                Benötigen Aufmerksamkeit
              </p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon stat-icon-purple">
              <Wallet size={24} />
            </div>
            <div className="stat-content">
              <h3>Ausgaben Monat</h3>
              <div className="stat-value">
                {formatCurrency(expenseStats?.month_total || 0)}
              </div>
              <p className="stat-subtitle">
                Jahr: {formatCurrency(expenseStats?.year_total || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="dashboard-grid">
          {/* Recent Invoices */}
          <div className="card dashboard-card">
            <div className="card-header">
              <h2>Aktuelle Rechnungen</h2>
              <Link to="/invoices" className="btn btn-outline btn-sm">
                Alle anzeigen
                <ArrowRight size={16} />
              </Link>
            </div>
            <div className="card-body">
              {stats?.recent_invoices?.length > 0 ? (
                <div className="invoice-list">
                  {stats.recent_invoices.slice(0, 5).map((invoice) => (
                    <div key={invoice.id} className="invoice-item">
                      <div className="invoice-info">
                        <div className="invoice-number">
                          <Link
                            to={`/invoices/${invoice.id}`}
                            className="invoice-link"
                          >
                            {invoice.invoice_number}
                          </Link>
                        </div>
                        <div className="invoice-customer">
                          {invoice.customer?.name}
                        </div>
                        <div className="invoice-date">
                          {formatDate(invoice.invoice_date)}
                        </div>
                      </div>
                      <div className="invoice-meta">
                        <div className="invoice-amount">
                          {formatCurrency(invoice.net_amount)}
                        </div>
                        <div className={getStatusBadgeClass(invoice.status)}>
                          {getStatusText(invoice.status)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <FileText size={48} className="empty-icon" />
                  <h3>Keine Rechnungen vorhanden</h3>
                  <p>Erstellen Sie Ihre erste Rechnung</p>
                  <Link to="/invoices/new" className="btn btn-primary">
                    <Plus size={16} />
                    Neue Rechnung
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Monthly Revenue Chart */}
          <div className="card dashboard-card">
            <div className="card-header">
              <h2>Monatlicher Umsatz</h2>
              <div className="revenue-trend">
                <TrendingUp size={16} />
                <span>{new Date().getFullYear()}</span>
              </div>
            </div>
            <div className="card-body">
              <div className="revenue-chart">
                {monthlyRevenue.map((month, index) => {
                  const maxRevenue = Math.max(...monthlyRevenue.map(m => m.revenue));
                  const height = maxRevenue > 0 ? (month.revenue / maxRevenue) * 100 : 0;
                  const monthNames = [
                    'Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun',
                    'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'
                  ];

                  return (
                    <div key={index} className="chart-bar-container">
                      <div
                        className="chart-bar"
                        style={{ height: `${Math.max(height, 2)}%` }}
                        title={`${monthNames[index]}: ${formatCurrency(month.revenue)}`}
                      />
                      <div className="chart-label">{monthNames[index]}</div>
                    </div>
                  );
                })}
              </div>
              <div className="chart-summary">
                <div className="chart-total">
                  <strong>Gesamtumsatz {new Date().getFullYear()}: </strong>
                  {formatCurrency(monthlyRevenue.reduce((sum, month) => sum + month.revenue, 0))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="card">
          <div className="card-header">
            <h2>Schnellaktionen</h2>
          </div>
          <div className="card-body">
            <div className="quick-actions">
              <Link to="/customers" className="quick-action">
                <Users size={24} />
                <div>
                  <h3>Kunden verwalten</h3>
                  <p>Kundendaten bearbeiten und neue Kunden anlegen</p>
                </div>
              </Link>
              <Link to="/invoices" className="quick-action">
                <FileText size={24} />
                <div>
                  <h3>Rechnungen verwalten</h3>
                  <p>Rechnungen erstellen, versenden und verwalten</p>
                </div>
              </Link>
              <Link to="/expenses" className="quick-action">
                <Receipt size={24} />
                <div>
                  <h3>Ausgaben erfassen</h3>
                  <p>Betriebsausgaben dokumentieren und Belege hinterlegen</p>
                </div>
              </Link>
              <Link to="/datev" className="quick-action">
                <Calendar size={24} />
                <div>
                  <h3>DATEV Export</h3>
                  <p>Daten für den Steuerberater exportieren</p>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;